// Pulse chart — the SVG drawing for the 8-week production line, shared by the
// field dashboard's project hero and the admin Today board's all-sites roll-up.
// Pure: {pulse} in, markup out. No DOM lookups, no Supabase — each page owns
// its own container, header and resize wiring; only the drawing lives here so
// the two boards can never drift apart.
//
// Drawn in PIXEL units (viewBox width = the container's real width, fixed
// height) so labels render at true UI size on every screen. An SVG sized only
// by viewBox has no intrinsic height in flex layout and gets clipped by the
// hero's overflow:hidden.

// Compact value: 1240 -> "1.2k". Used on point labels and legend chips.
export function fmtPulseValue(v) {
  return v >= 1000 ? (Math.round(v / 100) / 10) + 'k' : String(Math.round(v));
}

// Legend rows for a pulse: one chip per series with its WINDOW TOTAL.
// [{name, unit, value, colorIdx}] — the caller builds the DOM (material names
// are tenant data, so they go in via textContent, never innerHTML).
//
// The total, not the current week: a week that has only just started reads 0
// on every line, which left the whole panel with no quantity on it at all —
// the reader could see the shape of the pace but not how much anything was.
// The total always carries a real number, and the per-point labels on the
// chart give the week-by-week detail.
export function pulseLegend(pulse) {
  return (pulse.series || []).map((sr, si) => ({
    name: sr.name,
    unit: sr.unit,
    value: fmtPulseValue(sr.total),
    colorIdx: si,
  }));
}

// The chart itself. `width` is the container's pixel width; `label` prefixes
// the accessible description ("Production per week, last 8 weeks" by default).
export function pulseChartSvg(pulse, width, label = 'Production per week, last 8 weeks') {
  const series = pulse.series;
  const fmt    = fmtPulseValue;

  const W = Math.max(300, Math.round(width) || 640), H = 150;
  const top = 30, bottom = 24, pad = 34;
  const chartH = H - top - bottom;
  const baseY  = top + chartH;
  // One shared scale across series: the chart compares PACE shapes; exact
  // values (with units) live on the legend chips and the point labels.
  const max  = Math.max(...series.flatMap(sr => sr.values), 1);
  const step = (W - pad * 2) / (pulse.weeks.length - 1);
  const lbl  = w => {
    const [y, m, d] = w.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };
  // Narrow phones can't fit 8 week labels — thin to every 2nd, keep the last.
  const labelEvery = step < 46 ? 2 : 1;

  const toPts = vals => vals.map((v, i) => ({
    x: +(pad + step * i).toFixed(1),
    y: +(top + chartH - chartH * v / max).toFixed(1),
    v,
  }));

  let s = `<line x1="${pad - 8}" y1="${baseY}" x2="${W - pad + 8}" y2="${baseY}" class="fh-pulse-base"/>`;
  series.forEach((sr, si) => {
    // A caller that draws ONE series per card (Home's per-material charts)
    // still wants that material's own colour, not slot 0's. colorIdx is
    // optional: every existing caller passes the whole set and gets si.
    const ci = sr.colorIdx ?? si;
    const pts = toPts(sr.values);
    s += `<path d="${smoothPath(pts, top, baseY)}" pathLength="1" class="fh-pulse-line fh-pulse-line--s${ci}"/>`;
    s += `<g class="fh-pulse-pts">`;
    pts.forEach((p, i) => {
      const now = i === pts.length - 1;
      s += `<circle cx="${p.x}" cy="${p.y}" r="${now ? 4 : 2.5}" class="fh-pulse-dot fh-pulse-dot--s${ci}${now ? ' fh-pulse-dot--now' : ''}"/>`;
      // Week values are labeled on the LEAD series only (highest volume — the
      // line that sets the scale). Labeling every line was noise; labeling
      // none left a chart of pure shapes with no quantity anywhere on it.
      // Zeros stay unlabeled: a row of "0"s reads as clutter, and a flat line
      // on the baseline already says nothing was placed. On a narrow chart
      // they thin out on the same cadence as the week labels — at phone width
      // eight numbers collide with each other and with the curve.
      if (si === 0 && p.v > 0 && (now || (pts.length - 1 - i) % labelEvery === 0))
        s += `<text x="${p.x}" y="${p.y - (now ? 11 : 9)}" text-anchor="middle" class="fh-pulse-val${now ? ' fh-pulse-val--now' : ''}">${fmt(p.v)}</text>`;
    });
    s += '</g>';
  });
  toPts(series[0].values).forEach((p, i, arr) => {
    const now = i === arr.length - 1;
    if (now || (arr.length - 1 - i) % labelEvery === 0)
      s += `<text x="${p.x}" y="${H - 6}" text-anchor="middle" class="fh-pulse-week${now ? ' fh-pulse-week--now' : ''}">${lbl(pulse.weeks[i])}</text>`;
  });

  const a11y = label + '. ' + series.map(sr =>
    `${sr.name}: ${fmt(sr.total)}${sr.unit && sr.unit !== 'count' ? ' ' + sr.unit : ''} total, ` +
    `${fmt(sr.values[sr.values.length - 1])} this week`).join('; ');
  return `<svg viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" role="img" aria-label="${escAttr(a11y)}" class="fh-pulse-svg">${s}</svg>`;
}

// Minimal attribute escape — material names are tenant data.
function escAttr(t) {
  return String(t).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

// Catmull-Rom → cubic bézier so the trend reads as one calm stroke instead
// of a jagged polyline. Control points are clamped to the plot band so
// spiky data can't overshoot below the baseline (a line dipping under
// "zero" reads as a bug on a jobsite instrument).
function smoothPath(pts, minY, maxY) {
  if (pts.length < 3) return pts.map((p, i) => `${i ? 'L' : 'M'} ${p.x} ${p.y}`).join(' ');
  const cl = y => Math.min(maxY, Math.max(minY, y));
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = cl(p1.y + (p2.y - p0.y) / 6);
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = cl(p2.y - (p3.y - p1.y) / 6);
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x} ${p2.y}`;
  }
  return d;
}
